import { useMemo } from 'react';
import {
  HomeRegular, HomeFilled,
  ChartMultipleRegular, ChartMultipleFilled,
  LayerRegular, LayerFilled,
} from '@fluentui/react-icons';
import {
  TeamsAppLayout,
  type TeamsNavItem,
} from './TeamsAppLayout';
import { TEAMS_ROLE_BADGE_COLORS } from '../../styles/teams-theme';
import { useMeProfile } from '../../api/hooks';
import { useAuth } from '../../hooks/useAuth';
import type { UserRole } from '../../api/types';

/* Thin wrapper around the canonical LAYOUT-TEAMS-APP shell
   (.github/reference/teams-app/TeamsAppLayout.tsx). All this file does is
   translate our app's role model (SystemAdmin / Administrator / Guest) into
   nav-item visibility and the role-badge colour. Layout styling and structural
   rules live in TeamsAppLayout.tsx and must not be re-implemented here.

   DOR-F01: the Admin section has moved to Operations Manager (OPSM-F31). DOR is
   reporting-only, so no admin nav sub-items are rendered here. */

/* Transformr "T" mark (multi-colour brand SVG in public/). Rendered as an <img>
   so its own palette is preserved; sized by the sidebar's logoIcon class. */
function TransformrLogo({ className }: { className?: string }) {
  return <img src="/transformr-t-logo.svg" alt="Transformr" className={className} />;
}

function roleLabel(role: UserRole | undefined): string {
  if (role === 'SystemAdmin') return 'System Administrator';
  if (role === 'Administrator') return 'Administrator';
  if (role === 'Guest') return 'Guest';
  return 'User';
}

// Our three roles map onto the four keys in TEAMS_ROLE_BADGE_COLORS:
// SystemAdmin → admin (danger), Administrator → editor (brand), Guest → user (subtle).
function roleBadgeColor(role: UserRole | undefined):
  'danger' | 'important' | 'brand' | 'subtle' {
  if (role === 'SystemAdmin') return TEAMS_ROLE_BADGE_COLORS.admin;
  if (role === 'Administrator') return TEAMS_ROLE_BADGE_COLORS.editor;
  if (role === 'Guest') return TEAMS_ROLE_BADGE_COLORS.user;
  return TEAMS_ROLE_BADGE_COLORS.user;
}

export function Layout() {
  const { isReady, logoutRedirect } = useAuth();
  const { data: profile, isLoading: profileLoading } = useMeProfile();

  const navItems = useMemo<TeamsNavItem[]>(() => [
    { path: '/', label: 'Home', icon: HomeRegular, activeIcon: HomeFilled },
    { path: '/reports', label: 'Epic Reports', icon: ChartMultipleRegular, activeIcon: ChartMultipleFilled },
    {
      path: '/reports/features', label: 'Feature Reports', icon: LayerRegular, activeIcon: LayerFilled,
      // The Feature Progress drill-down lives under the project→epic URL hierarchy
      // (/reports/projects/:p/epics/:e/features/:f/progress) but belongs to this
      // nav item — so claim active state there too.
      activeMatch: (p) => /^\/reports\/projects\/[^/]+\/epics\/[^/]+\/features\//.test(p),
    },
    // Programme reports are not implemented yet — nav item hidden until they ship.
  ], []);

  // MSAL redirect-based sign-out; returns to the app origin (postLogoutRedirectUri).
  const handleSignOut = () => { void logoutRedirect(); };

  return (
    <TeamsAppLayout
      productName="DevOps Reports"
      // Company name of the signed-in user (DorCustomers.Name). Falls back to the
      // Transformr brand until the profile resolves / if no company is set.
      productSubtitle={profile?.customerName ?? 'Transformr'}
      LogoIcon={TransformrLogo}
      navItems={navItems}
      showAdminSection={false}
      adminItems={[]}
      user={profile ? { displayName: profile.displayName, email: profile.email } : null}
      userRole={roleLabel(profile?.role)}
      roleBadgeColor={roleBadgeColor(profile?.role)}
      isLoading={!isReady || profileLoading}
      onSignOut={handleSignOut}
    />
  );
}
